import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MapPin, Users, Search, Filter, Grid, List, Home, AlertCircle, Crown, Calendar, ArrowLeft } from 'lucide-react';
import { useGetUserApartmentsQuery, useGetUserMembershipStatusQuery } from '../../store/slices/apiSlice';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import toast from 'react-hot-toast';
import { usePageMetadata } from '../../hooks/usePageMetadata';
import Navbar from '../../components/common/Navbar';

const Apartments: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [location, setLocation] = useState('');
  const [capacity, setCapacity] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 12;

  usePageMetadata('apartments');

  // Check membership status
  const { data: membershipStatusResponse } = useGetUserMembershipStatusQuery(undefined, {
    skip: !user,
  });

  const membershipStatus = membershipStatusResponse?.data;
  const hasActiveMembership = membershipStatus?.is_active || false;

  const { data: apartmentsResponse, isLoading } = useGetUserApartmentsQuery({
    page: currentPage,
    limit: itemsPerPage,
    location: location || undefined,
    capacity: capacity ? Number(capacity) : undefined,
    search: searchTerm || undefined,
  }, {
    skip: !user || !hasActiveMembership,
    refetchOnMountOrArgChange: true,
  });

  const apartments = apartmentsResponse?.data || [];
  const pagination = {
    current_page: 1,
    total_pages: 1,
    total_records: apartments.length,
  };

  useEffect(() => {
    if (!user) {
      toast.error('Please login to view apartments');
      if (window.innerWidth < 1024) {
        window.location.href = "https://app.diasporawise.com";
      } else {
        navigate('/');
      }
      return;
    }

    if (!hasActiveMembership) {
      toast.error('You need an active membership to book apartments');
      navigate('/membership/plans');
    }
  }, [user, hasActiveMembership, navigate]);

  const getPrimaryImage = (images: string[]) => {
    if (!images || images.length === 0) {
      return 'https://via.placeholder.com/400x300/FFFFFF/CCCCCC?text=No+Image';
    }
    return images[0];
  };

  if (!user) {
    return null;
  }

  if (!hasActiveMembership) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-12 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Card className="p-12">
              <Crown className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Membership Required</h2>
              <p className="text-gray-600 mb-6">
                You need an active membership to access apartment bookings. Purchase a membership plan to get started.
              </p>
              <Button onClick={() => navigate('/membership/plans')} className="bg-gray-800 hover:bg-gray-900">
                View Membership Plans
              </Button>
            </Card>
          </div>
        </div>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          {/* Back Button */}
          <div className="mb-4">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Available Apartments</h1>
              <p className="text-gray-600">Browse and book from our selection of premium apartments</p>
            </div>
            <Button
              onClick={() => navigate('/apartments/my-bookings')}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Calendar className="w-4 h-4" />
              My Bookings
            </Button>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search apartments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              />
            </div>

            {/* Location Filter */}
            <input
              type="text"
              placeholder="Location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
            />

            {/* Capacity Filter */}
            <input
              type="number"
              placeholder="Capacity"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              min="1"
            />

            {/* View Mode Toggle */}
            <div className="flex gap-2 border border-gray-300 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'grid' ? 'bg-gray-800 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'list' ? 'bg-gray-800 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Showing {apartments.length} {apartments.length === 1 ? 'apartment' : 'apartments'}
          </p>
        </div>

        {/* Apartments Grid/List */}
        {apartments.length === 0 ? (
          <Card className="p-12 text-center">
            <Home className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Apartments Found</h3>
            <p className="text-gray-600">
              {searchTerm || location || capacity
                ? 'Try adjusting your search criteria'
                : 'No apartments available at the moment'}
            </p>
          </Card>
        ) : (
          <div
            className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'
                : 'space-y-4'
            }
          >
            {apartments.map((apartment) => (
              <Link
                key={apartment.apartment_id}
                to={`/apartments/${apartment.apartment_id}`}
                className="block"
              >
                <Card className="overflow-hidden hover:shadow-lg transition-shadow">
                  {/* Image */}
                  <div className="relative h-48 bg-gray-200">
                    <img
                      src={getPrimaryImage(apartment.images || [])}
                      alt={apartment.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300/FFFFFF/CCCCCC?text=No+Image';
                      }}
                    />
                    {apartment.is_available ? (
                      <div className="absolute top-3 right-3">
                        <Badge variant="success" className="bg-green-500 text-white">
                          Available
                        </Badge>
                      </div>
                    ) : (
                      <div className="absolute top-3 right-3">
                        <Badge variant="danger" className="bg-red-500 text-white">
                          Unavailable
                        </Badge>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                      {apartment.name}
                    </h3>
                    {apartment.description && (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {apartment.description}
                      </p>
                    )}

                    {/* Details */}
                    <div className="space-y-2 mb-4">
                      {apartment.location && (
                        <div className="flex items-center text-sm text-gray-600">
                          <MapPin className="w-4 h-4 mr-2" />
                          <span className="truncate">{apartment.location}</span>
                        </div>
                      )}
                      <div className="flex items-center text-sm text-gray-600">
                        <Users className="w-4 h-4 mr-2" />
                        <span>Capacity: {apartment.capacity} guests</span>
                      </div>
                    </div>

                    {/* View Details Button */}
                    <button className="w-full bg-gray-800 text-white py-2 px-4 rounded-lg hover:bg-gray-900 transition-colors text-sm font-medium">
                      View Details & Book
                    </button>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}

      </div>
      </div>
    </>
  );
};

export default Apartments;
